import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Calendar, Award, Filter } from 'lucide-react';
import apiClient from '../../utils/api';
import './AdminCreditHistory.css';

function AdminCreditHistory() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterUserId, setFilterUserId] = useState('');

  useEffect(() => {
    loadHistory();
  }, [filterUserId]);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const url = filterUserId 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/credits/transactions?user_id=${filterUserId}&limit=100`
        : `${process.env.REACT_APP_BACKEND_URL}/api/admin/credits/transactions?limit=100`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      setTransactions(data.transactions || []);
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'addition':
      case 'purchase':
      case 'subscription_grant':
      case 'admin_grant':
        return <TrendingUp className="transaction-icon positive" />;
      case 'deduction':
      case 'chat_message':
      case 'admin_deduct':
        return <TrendingDown className="transaction-icon negative" />;
      case 'daily_bonus':
        return <Award className="transaction-icon bonus" />;
      default:
        return <Calendar className="transaction-icon neutral" />;
    }
  };

  const getTransactionColor = (type) => {
    if (['addition', 'purchase', 'subscription_grant', 'admin_grant', 'daily_bonus'].includes(type)) {
      return 'positive';
    }
    return 'negative';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getTypeLabel = (type) => {
    const labels = {
      'purchase': 'Coin-Kauf',
      'subscription_grant': 'Abo-Credits',
      'admin_grant': 'Admin-Gutschrift',
      'admin_deduct': 'Admin-Abzug',
      'chat_message': 'Chat-Nachricht',
      'daily_bonus': 'Tagesbonus',
      'addition': 'Gutschrift',
      'deduction': 'Abzug'
    };
    return labels[type] || type;
  };

  const getCreditTypeLabel = (type) => {
    const labels = {
      'daily': 'Täglich',
      'monthly': 'Monatlich',
      'permanent': 'Permanent'
    };
    return labels[type] || type;
  };

  return (
    <div className="admin-credit-history" data-testid="admin-credit-history">
      <div className="history-header">
        <h2>💰 Credit-Transaktionen</h2>
        <div className="filter-bar">
          <Filter size={18} />
          <input
            type="text"
            placeholder="User-ID filtern..."
            value={filterUserId}
            onChange={(e) => setFilterUserId(e.target.value)}
            className="filter-input"
          />
          {filterUserId && (
            <button onClick={() => setFilterUserId('')} className="btn-clear-filter">
              ✕
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Lade Transaktionen...</p>
        </div>
      ) : transactions.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📊</div>
          <p>Keine Transaktionen gefunden</p>
        </div>
      ) : (
        <div className="transactions-table-container">
          <table className="transactions-table">
            <thead>
              <tr>
                <th>Datum</th>
                <th>User ID</th>
                <th>Typ</th>
                <th>Credit-Typ</th>
                <th>Beschreibung</th>
                <th>Betrag</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((tx) => (
                <tr 
                  key={tx.id} 
                  className={getTransactionColor(tx.transaction_type)}
                  data-testid={`admin-transaction-${tx.id}`}
                >
                  <td className="date-cell">{formatDate(tx.created_at)}</td>
                  <td className="user-id-cell">{tx.user_id.substring(0, 8)}...</td>
                  <td>
                    <span className="type-badge">
                      {getTypeLabel(tx.transaction_type)}
                    </span>
                  </td>
                  <td>
                    <span className="credit-type-badge-small">
                      {getCreditTypeLabel(tx.credit_type)}
                    </span>
                  </td>
                  <td className="description-cell">
                    {tx.reason || tx.description || '-'}
                  </td>
                  <td className={`amount-cell ${tx.amount > 0 ? 'positive' : 'negative'}`}>
                    {tx.amount > 0 ? '+' : ''}{tx.amount}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default AdminCreditHistory;
